import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArticleDetail from '../components/article/ArticleDetail';
import RelatedArticles from '../components/article/RelatedArticles';
import ShareButtons from '../components/article/ShareButtons';
import LoadingSpinner from '../components/common/LoadingSpinner';
import * as api from '../services/api';
import { adaptArticleData, adaptArticlesData } from '../services/articleAdapter';
import { useArticleViews } from '../hooks/useArticleViews';

// Cache pour les articles individuels
const articleCache = new Map();
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes pour les articles

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Hook pour gérer l'incrémentation des vues - ne s'active que quand l'article est chargé
  useArticleViews(article?.id && !loading ? article.id : null, 5000);

  // Mémoriser l'URL de partage pour éviter les recalculs
  const shareUrl = useMemo(() => window.location.href, []);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) return;

      try {
        setLoading(true);
        setError(null);

        // Vérifier le cache d'abord
        const cacheKey = `article-${slug}`;
        const cached = articleCache.get(cacheKey);
        
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          setArticle(cached.article);
          setRelatedArticles(cached.relatedArticles);
          setLoading(false);
          return;
        }

        // Paralléliser les requêtes pour l'article et les articles liés
        const articlePromise = api.fetchArticleBySlug(slug);
        
        const apiArticleData = await articlePromise;

        if (!apiArticleData) {
          navigate('/404');
          return;
        }

        const adaptedArticle = adaptArticleData(apiArticleData);
        setArticle(adaptedArticle);

        // Charger les articles liés en parallèle (ne pas attendre)
        api.fetchRelatedArticles(
          adaptedArticle.id,
          adaptedArticle.categories,
          4
        ).then(relatedData => {
          const adaptedRelated = adaptArticlesData(relatedData?.articles || relatedData || []);
          setRelatedArticles(adaptedRelated);
          
          // Mettre en cache les données complètes
          articleCache.set(cacheKey, {
            article: adaptedArticle,
            relatedArticles: adaptedRelated,
            timestamp: Date.now()
          });
        }).catch(err => {
          console.warn('Erreur lors du chargement des articles liés:', err);
          // Ne pas bloquer l'affichage pour les articles liés
        });
        
      } catch (err) {
        console.error("Erreur lors du chargement de l'article:", err);
        if (err.response?.status === 404) {
          navigate('/404');
        } else {
          setError("Impossible de charger l'article");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, navigate]);

  // Affichage conditionnel optimisé
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Erreur</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb optimisé */}
        <nav className="text-sm text-gray-600 mb-6">
          <span className="font-medium">Catégorie: </span>
          {article.categories?.length > 0 
            ? article.categories.map(cat => cat.name).join(', ')
            : 'Non catégorisé'
          }
        </nav>

        {/* Contenu principal de l'article */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
          <ArticleDetail article={article} />
        </div>

        {/* Boutons de partage */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <ShareButtons title={article.title} slug={article.slug} />
        </div>

        {/* Articles liés - Afficher même s'ils ne sont pas encore chargés */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <RelatedArticles 
            articles={relatedArticles} 
            loading={relatedArticles.length === 0}
          />
        </div>
      </article>
    </div>
  );
};

export default ArticlePage;